import React, { useState, useEffect, useRef, useCallback, useMemo, memo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2, Download, Save, Plus, Upload, Trash2, Film, Image as ImageIcon, Copy, Check, GripVertical, Sun, Moon, Aperture, Video, FileText, Smartphone, Share2, Link, X, Play, Clapperboard, Pencil, MessageSquare, Eye, EyeOff, Lock, Unlink, CopyPlus } from 'lucide-react';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragOverlay
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates, rectSortingStrategy, useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import Layout from '../components/Layout';
import LoginModal from '../components/LoginModal';
import { useAuth } from '../contexts/AuthContext';
import { getProject, updateShots, exportProjectPDF, uploadShotImage, appendVideoToProject, createShareLink, getShareLink, deleteShareLink, duplicateProject, getProjectComments, setProjectBlanking } from '../lib/api';
import api from '../lib/api';
import ShotEditorModal from '../components/ShotEditorModal';
import DavinciExportModal from '../components/DavinciExportModal';
import { toast } from 'sonner';
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { cn, formatTime, formatDuration } from '../lib/utils';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator
} from "../components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";

const ADMIN_EMAIL = (process.env.REACT_APP_ADMIN_EMAIL || '').trim().toLowerCase();

// --- INSERT BUTTON (visual insert zone between cards) ---
function InsertButton({ index, handleInsertAt }) {
    return (
        <div 
            className="absolute -right-[16px] top-0 bottom-0 w-[32px] z-40 flex items-center justify-center group/ins cursor-pointer"
            onClick={() => handleInsertAt(index, 'blank')}
            data-testid={`insert-btn-${index}`}
        >
            {/* Vertical pill - visible at rest, expands on hover */}
            <div className="relative flex items-center justify-center w-[6px] h-[40%] min-h-[50px] max-h-[80px] rounded-full bg-muted-foreground/25 group-hover/ins:bg-muted-foreground/50 group-hover/ins:w-[24px] group-hover/ins:h-[48px] transition-all duration-200 ease-out">
                {/* Plus sign - visible on hover */}
                <span className="text-base font-black text-transparent group-hover/ins:text-foreground/80 select-none leading-none transition-all duration-200">+</span>
            </div>
        </div>
    );
}

// --- KINOTE TAG ---
function Tag({ children, icon: Icon, className }) {
    return (
        <span className={cn("inline-flex items-center gap-1 text-sm px-1.5 py-0.5 rounded font-medium", className)}>
            {Icon && <Icon className="w-3.5 h-3.5" />}
            {children}
        </span>
    );
}

// --- ADD SHOT CARD (placeholder card in grid) ---
function AddShotCard({ onAddBlank, onImportVideo }) {
    return (
        <div 
            className="bg-secondary/30 border-2 border-dashed border-border rounded-xl overflow-hidden flex flex-col h-full"
            data-testid="add-shot-card"
        >
            {/* Header */}
            <div className="px-3 py-2 flex justify-between items-center text-xs text-muted-foreground border-b border-border/30">
                <span className="font-medium text-muted-foreground/50">Nouveau</span>
            </div>
            
            {/* Two options side by side */}
            <div className="flex-1 flex flex-col gap-2 p-3">
                {/* Import Video Option */}
                <button 
                    onClick={onImportVideo}
                    className="group flex-1 flex flex-col items-center justify-center gap-2 rounded-lg bg-secondary/40 hover:bg-primary/10 border border-transparent hover:border-primary/30 transition-all duration-200 cursor-pointer"
                    data-testid="import-video-btn"
                >
                    <div className="w-12 h-12 rounded-xl bg-border/50 group-hover:bg-primary/20 flex items-center justify-center transition-all duration-200">
                        <Video className="w-6 h-6 text-muted-foreground/40 group-hover:text-primary transition-colors" />
                    </div>
                    <span className="text-xs text-muted-foreground/60 group-hover:text-foreground/80 transition-colors font-medium">
                        Importer une vidéo
                    </span>
                </button>
                
                {/* Add Blank Shot Option */}
                <button 
                    onClick={onAddBlank}
                    className="group flex-1 flex flex-col items-center justify-center gap-2 rounded-lg bg-secondary/40 hover:bg-primary/10 border border-transparent hover:border-primary/30 transition-all duration-200 cursor-pointer"
                    data-testid="add-blank-shot-btn"
                >
                    <div className="w-12 h-12 rounded-xl bg-border/50 group-hover:bg-primary/20 flex items-center justify-center transition-all duration-200">
                        <Plus className="w-6 h-6 text-muted-foreground/40 group-hover:text-primary transition-colors" />
                    </div>
                    <span className="text-xs text-muted-foreground/60 group-hover:text-foreground/80 transition-colors font-medium">
                        Plan vierge
                    </span>
                </button>
            </div>
        </div>
    );
}

// --- SORTABLE SHOT CARD (memoized for performance) ---
const SortableShot = memo(function SortableShot({ shot, index, isSelected, toggleSelection, selectSingle, triggerCardUpload, triggerEditor, handleDuplicate, handleDelete, handleInsertAt, isOverlay, shotAspect, imgFit = 'object-cover' }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: shot.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 100 : "auto",
        opacity: isDragging ? 0.3 : 1,
    };

    if (isOverlay) {
        style.zIndex = 999; style.opacity = 1; style.transform = undefined;
        style.cursor = "grabbing"; style.scale = 1.02;
    }

    const stopProp = (e) => { e.preventDefault(); e.stopPropagation(); };
    const isDay = shot.lighting === 'Day' || shot.lighting === 'Ext. Day' || !shot.lighting;

    return (
        <div ref={setNodeRef} style={style} className="relative group h-full touch-none" data-testid={`shot-card-${index}`}>
            {/* Selection corner - clickable zone for multi-select */}
            <div 
                className="absolute top-0 left-0 z-20 overflow-hidden rounded-tl-xl cursor-pointer"
                style={{ width: '32px', height: '32px' }}
                onClick={(e) => { e.stopPropagation(); toggleSelection(shot.id, true); }}
                data-testid={`select-corner-${index}`}
            >
                {isSelected ? (
                    <>
                        <div
                            style={{
                                width: 0,
                                height: 0,
                                borderStyle: 'solid',
                                borderWidth: '32px 32px 0 0',
                                borderColor: 'hsl(var(--highlight)) transparent transparent transparent',
                            }}
                        />
                        <Check className="absolute w-3.5 h-3.5 text-white" style={{ top: '3px', left: '3px' }} strokeWidth={3} />
                    </>
                ) : (
                    <div className="w-full h-full opacity-20 group-hover:opacity-100 transition-opacity">
                        <div
                            style={{
                                width: 0,
                                height: 0,
                                borderStyle: 'solid',
                                borderWidth: '32px 32px 0 0',
                                borderColor: 'hsl(var(--muted-foreground) / 0.2) transparent transparent transparent',
                            }}
                        />
                    </div>
                )}
            </div>
            <div className={cn(
                "flex-1 bg-card rounded-xl overflow-hidden flex flex-col h-full select-none transition-all duration-200",
                isSelected 
                    ? "ring-2 ring-highlight shadow-lg shadow-highlight/20" 
                    : "border border-border hover:shadow-md card-shadow",
                isOverlay && "ring-2 ring-highlight shadow-xl shadow-highlight/25"
            )} onClick={(e) => {
                if (e.ctrlKey || e.metaKey) {
                    toggleSelection(shot.id, true);
                } else {
                    selectSingle(shot.id);
                }
            }}>
                {/* Header */}
                <div className="px-3 py-2 flex justify-between items-center text-sm text-muted-foreground cursor-grab active:cursor-grabbing border-b border-border/50" {...attributes} {...listeners}>
                    <div className="flex items-center gap-2">
                        <span className="font-semibold text-foreground pl-3">#{index + 1}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <span className="text-xs bg-secondary px-1.5 py-0.5 rounded">{formatDuration(shot.duration)}</span>
                        <GripVertical className="w-3 h-3 opacity-20" />
                    </div>
                </div>
                
                {/* Thumbnail — always 16:9 frame, object-contain for portrait projects */}
                <div className="aspect-video bg-secondary/20 relative overflow-hidden">
                    {shot.frame_path_temp ? (
                        <img src={shot.frame_path_temp} className={`w-full h-full ${imgFit}`} draggable={false} alt="" />
                    ) : shot.frame_path ? (
                        <img src={(process.env.REACT_APP_BACKEND_URL || '') + shot.frame_path} className={`w-full h-full ${imgFit}`} draggable={false} alt="" onError={(e) => e.target.src = ""} />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center">
                            <span className="text-xs text-muted-foreground/30">No Image</span>
                        </div>
                    )}

                    {!isDragging && !isOverlay && (
                        <div className="absolute top-1.5 right-1.5 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-50">
                            {[
                                { icon: Pencil, action: () => triggerEditor(shot.id), title: "Editer la vignette", tid: `edit-btn-${index}` },
                                { icon: Upload, action: () => triggerCardUpload(shot.id), title: "Upload", tid: `upload-img-${index}` },
                                { icon: Copy, action: () => handleDuplicate(new Set([shot.id])), title: "Duplicate", tid: `duplicate-btn-${index}` },
                                { icon: Trash2, action: () => handleDelete(new Set([shot.id])), title: "Delete", tid: `delete-btn-${index}`, danger: true },
                            ].map(({ icon: I, action, title, tid, danger, accent }) => (
                                <button key={tid} className={cn("h-6 w-6 rounded-md flex items-center justify-center bg-card/90 backdrop-blur-sm border border-border/50 transition-colors", danger ? "hover:bg-destructive hover:text-white hover:border-destructive" : accent ? "hover:bg-primary hover:text-white hover:border-primary" : "hover:bg-card")}
                                    title={title} onPointerDown={stopProp} onMouseDown={stopProp} onClick={(e) => { stopProp(e); action(); }} data-testid={tid}>
                                    <I className="w-3 h-3" />
                                </button>
                            ))}
                        </div>
                    )}
                </div>
                
                {/* Footer */}
                <div className="p-3 flex-1 flex flex-col gap-2 min-h-[90px]">
                    <p className="font-typewriter text-sm text-foreground/85 line-clamp-2 leading-relaxed" data-testid={`shot-description-${index}`}>
                        {shot.description || "No description"}
                    </p>
                    
                    {/* Tags - KINOTE style: low saturation, 10% bg opacity */}
                    <div className="mt-auto flex gap-1 flex-wrap">
                        <Tag icon={Film} className="text-foreground/60 bg-foreground/[0.06]">{shot.shot_type}</Tag>
                        <Tag icon={Video} className="text-foreground/60 bg-foreground/[0.06]">{shot.camera_movement || 'Static'}</Tag>
                        <Tag icon={Aperture} className="text-foreground/60 bg-foreground/[0.06]">{shot.focal_length || '50mm'}</Tag>
                        <Tag icon={isDay ? Sun : Moon} className="text-foreground/60 bg-foreground/[0.06]">{shot.lighting || 'Day'}</Tag>
                    </div>
                </div>
            </div>

            {!isDragging && !isOverlay && handleInsertAt && (
                <InsertButton index={index} handleInsertAt={handleInsertAt} />
            )}
        </div>
    );
});

// --- MAIN ---
export default function ProjectView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const isAdmin = !!user?.email && (user.email || '').trim().toLowerCase() === ADMIN_EMAIL;
  const [project, setProject] = useState(null);
  const [shots, setShots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [selectedShotIds, setSelectedShotIds] = useState(new Set());
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  // File picked from the timeline import button is held here until the user
  // picks a sensitivity in the dialog — same level of control as the initial
  // upload offers.
  const [pendingVideoFile, setPendingVideoFile] = useState(null);
  const fileInputRef = useRef(null);
  const cardFileInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const [uploadTargetId, setUploadTargetId] = useState(null);

  // Autosave state — initialShotsLoaded gates the effect against the very
  // first server fetch, lastSavedShotsJsonRef is the diff baseline so we only
  // PUT when shots actually change locally (not when polling reloads them).
  const initialShotsLoaded = useRef(false);
  const lastSavedShotsJsonRef = useRef('');
  
  // Share state
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareToken, setShareToken] = useState(null);
  const [allowPdfExport, setAllowPdfExport] = useState(true);
  const [allowZipExport, setAllowZipExport] = useState(true);
  const [sharePasswordEnabled, setSharePasswordEnabled] = useState(false);
  const [sharePassword, setSharePassword] = useState('');
  const [shareCommentsEnabled, setShareCommentsEnabled] = useState(false);
  const [shareThumbsEnabled, setShareThumbsEnabled] = useState(true);
  const [shareEditEnabled, setShareEditEnabled] = useState(false);
  const [shareDuplicateEnabled, setShareDuplicateEnabled] = useState(false);
  const [sharingLoading, setSharingLoading] = useState(false);
  const [shareHasPassword, setShareHasPassword] = useState(false); // existing hash on server

  const [showEditor, setShowEditor] = useState(false);
  const [editorTargetShotId, setEditorTargetShotId] = useState(null);
  const [duplicatingProject, setDuplicatingProject] = useState(false);

  // Derived — project is now declared above so no TDZ issue.
  const isOwner = project?.is_owner !== false;
  const writeGuard = useCallback(() => {
    if (project && project.is_owner === false) {
      toast.error("Dupliquez le projet pour pouvoir le modifier");
      return false;
    }
    return true;
  }, [project]);
  const handleDuplicateProject = async (e) => {
    e?.stopPropagation?.();
    setDuplicatingProject(true);
    const tid = toast.loading('Duplication en cours…');
    try {
      const copy = await duplicateProject(id);
      toast.dismiss(tid);
      toast.success('Projet dupliqué — redirection…');
      navigate(`/project/${copy.id}`);
    } catch (err) {
      toast.dismiss(tid);
      toast.error(err?.response?.data?.detail || `Échec de la duplication (${err?.response?.status ?? 'réseau'})`);
    } finally { setDuplicatingProject(false); }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => { let i; if (project?.status === 'processing') i = setInterval(loadProjectData, 3000); return () => clearInterval(i); }, [project?.status]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadProjectData(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadProjectData = async () => {
    try {
      const data = await getProject(id);
      setProject(data);
      if (shots.length === 0 || (project?.status === 'processing' && data.status === 'ready')) {
        const incoming = data.shots || [];
        setShots(incoming);
        // Sync the autosave baseline so the upcoming effect run doesn't trigger
        // a redundant PUT right after fetch.
        lastSavedShotsJsonRef.current = JSON.stringify(incoming);
        initialShotsLoaded.current = true;
      }
    } catch (e) {
      if (e?.response?.status === 401) { navigate('/', { replace: true }); return; }
      toast.error("Échec du chargement du projet");
    } finally { setLoading(false); }
  };

  // Debounced autosave: persists any local change to shots (creation, edit,
  // reorder, duplication, deletion) ~1s after the user stops. Without this,
  // a freshly inserted blank shot would be lost on hard refresh because it
  // only lived in React state.
  useEffect(() => {
    if (!initialShotsLoaded.current || !id) return;
    const json = JSON.stringify(shots);
    if (json === lastSavedShotsJsonRef.current) return;

    const handle = setTimeout(async () => {
      if (project && project.is_owner === false) return; // read-only project
      try {
        await updateShots(id, shots);
        lastSavedShotsJsonRef.current = json;
      } catch { /* silent — manual Save button still works */ }
    }, 1000);
    return () => clearTimeout(handle);
  }, [shots, id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDragStart = (e) => setActiveId(e.active.id);
  const handleDragEnd = (e) => {
    const { active, over } = e;
    if (active.id !== over?.id) setShots((items) => arrayMove(items, items.findIndex(i => i.id === active.id), items.findIndex(i => i.id === over.id)));
    setActiveId(null);
  };
  
  const handleSave = async () => { if (!writeGuard()) return; setSaving(true); try { const u = await updateShots(id, shots); setShots(u.shots); toast.success("Enregistré"); } catch { toast.error("Échec de l'enregistrement"); } finally { setSaving(false); } };
  const toggleSelection = useCallback((sid, multi) => { setSelectedShotIds(prev => { const s = new Set(multi ? prev : []); if (s.has(sid)) s.delete(sid); else s.add(sid); return s; }); }, []);
  const selectSingle = useCallback((sid) => setSelectedShotIds(new Set([sid])), []);
  const handleShotUpdate = useCallback((field, value) => { if (!writeGuard()) return; setShots(prev => prev.map(s => selectedShotIds.has(s.id) ? { ...s, [field]: value } : s)); }, [selectedShotIds, writeGuard]);

  const handleInsertAt = useCallback((index, type) => {
      if (!writeGuard()) return;
      const ns = { id: `temp-${Date.now()}-${Math.random()}`, start_time: 0, end_time: 0, duration: 2.0, frame_path: "", shot_type: "Wide", description: type === 'blank' ? "" : "Imported Clip", dialogue: "", camera_movement: "Static", focal_length: "50mm", lighting: "Day", notes: "" };
      setShots(prev => { const arr = [...prev]; if (index === -1) arr.push(ns); else arr.splice(index + 1, 0, ns); return arr; });
      setSelectedShotIds(new Set([ns.id])); toast.success("Plan ajouté");
  }, [writeGuard]);

  const handleDuplicate = useCallback((ids = selectedShotIds) => {
      if (!writeGuard()) return;
      if (ids.size === 0) return;
      setShots(prev => {
        const f = [];
        prev.forEach(s => {
          f.push(s);
          if (ids.has(s.id)) {
            const dup = { ...s, id: `copy-${Date.now()}-${Math.random()}`, description: `${s.description} (Copy)` };
            // Copy the AI editor config (prompt, refs, options, sketch) but
            // drop the generated image — the duplicate should regenerate.
            if (s.ai_config) {
              dup.ai_config = {
                ...s.ai_config,
                attachments: { ...(s.ai_config.attachments || {}), last_generated_url: null },
              };
            }
            f.push(dup);
          }
        });
        return f;
      });
      toast.success(`${ids.size} plan(s) dupliqué(s)`);
  }, [selectedShotIds, writeGuard]);

  // ── Share functions ───────────────────────────────────────────────────────
  const loadShareLink = async () => {
      try {
          const data = await getShareLink(id);
          if (data.token) {
              setShareToken(data.token);
              setAllowPdfExport(data.allow_pdf_export ?? true);
              setAllowZipExport(data.allow_zip_export ?? true);
              setSharePasswordEnabled(data.password_enabled ?? false);
              setShareHasPassword(data.password_enabled ?? false);
              setShareCommentsEnabled(data.comments_enabled ?? false);
              setShareThumbsEnabled(data.allow_thumbnails_export ?? true);
              setShareEditEnabled(data.allow_edit_export ?? false);
              setShareDuplicateEnabled(data.allow_project_duplicate ?? false);
              setSharePassword('');
          }
      } catch (e) { /* silent */ }
  };

  const handleOpenShareModal = async () => {
      setShowShareModal(true);
      await loadShareLink();
  };

  // Save (create or update) the share settings.
  const handleSaveShareSettings = async () => {
      setSharingLoading(true);
      try {
          await updateShots(id, shots);
          const payload = {
              allow_pdf_export: allowPdfExport,
              allow_zip_export: allowZipExport,
              allow_thumbnails_export: shareThumbsEnabled,
              allow_edit_export: shareEditEnabled,
              allow_project_duplicate: shareDuplicateEnabled,
              password_enabled: sharePasswordEnabled,
              password: sharePasswordEnabled ? (sharePassword || undefined) : undefined,
              comments_enabled: shareCommentsEnabled,
          };
          const data = await createShareLink(id, payload);
          setShareToken(data.token);
          setShareHasPassword(data.password_enabled ?? false);
          setSharePassword('');
          toast.success(shareToken ? "Paramètres de partage mis à jour" : "Lien de partage créé !");
      } catch (e) {
          toast.error("Erreur lors de l'enregistrement du partage");
      } finally {
          setSharingLoading(false);
      }
  };

  const handleDeleteShareLink = async () => {
      if (!window.confirm('Supprimer le lien de partage ? Les commentaires associés seront supprimés.')) return;
      setSharingLoading(true);
      try {
          await deleteShareLink(id);
          setShareToken(null);
          setSharePasswordEnabled(false);
          setShareHasPassword(false);
          setShareCommentsEnabled(false);
          setShareThumbsEnabled(true);
          setShareEditEnabled(false);
          setShareDuplicateEnabled(false);
          setSharePassword('');
          toast.success("Lien de partage supprimé");
      } catch (e) {
          toast.error("Erreur lors de la suppression");
      } finally {
          setSharingLoading(false);
      }
  };

  const copyShareLink = () => {
      const url = `${window.location.origin}/shared/${shareToken}`;
      navigator.clipboard.writeText(url);
      toast.success("Lien copié !");
  };

  // Open the layered thumbnail editor for the selected shot. Unsaved temp
  // shots have to be persisted first so the backend has an id to attach the
  // scene + flat PNG to.
  const triggerEditor = useCallback(async (shotId) => {
      let target = shotId;
      if (target.startsWith('temp-') || target.startsWith('copy-')) {
          try {
              const sp = await updateShots(id, shots);
              setShots(sp.shots);
              const idx = shots.findIndex((s) => s.id === target);
              if (idx !== -1 && sp.shots[idx]) target = sp.shots[idx].id;
          } catch {
              toast.error("Impossible d'enregistrer le plan avant édition");
              return;
          }
      }
      setEditorTargetShotId(target);
      setShowEditor(true);
  }, [id, shots]);

  const handleEditorSaved = useCallback((updatedProject) => {
      if (updatedProject?.shots) setShots(updatedProject.shots);
  }, []);

  const handleDelete = async (ids = selectedShotIds) => {
      if (!writeGuard()) return;
      if (ids.size === 0) return;
      if (!window.confirm(`Delete ${ids.size} shot(s)?`)) return;
      const ns = shots.filter(s => !ids.has(s.id)); setShots(ns); setSelectedShotIds(new Set());
      try { await updateShots(id, ns); toast.success("Supprimé"); } catch { toast.error("Échec de la synchronisation"); }
  };

  const triggerCardUpload = (sid) => { setUploadTargetId(sid); setTimeout(() => cardFileInputRef.current?.click(), 0); };
  const handleImageUpload = async (e) => {
      if (!writeGuard()) { if (cardFileInputRef.current) cardFileInputRef.current.value = ""; return; }
      const file = e.target.files[0]; const tid = uploadTargetId || (selectedShotIds.size === 1 ? [...selectedShotIds][0] : null);
      if (!file || !tid) return;
      setShots(shots.map(s => s.id === tid ? { ...s, frame_path_temp: URL.createObjectURL(file) } : s));
      const lt = toast.loading("Envoi en cours...");
      try {
          let cid = tid;
          if (tid.startsWith('temp-') || tid.startsWith('copy-')) { const sp = await updateShots(id, shots); setShots(sp.shots); const idx = shots.findIndex(s => s.id === tid); if (idx !== -1 && sp.shots[idx]) cid = sp.shots[idx].id; }
          const up = await uploadShotImage(id, cid, file); setShots(up.shots); toast.dismiss(lt); toast.success("Image mise à jour");
      } catch { toast.dismiss(lt); toast.error("Échec de l'envoi"); }
      finally { setUploadTargetId(null); if (cardFileInputRef.current) cardFileInputRef.current.value = ""; }
  };

  const handleVideoImport = (e) => {
      const file = e.target.files[0];
      if (videoInputRef.current) videoInputRef.current.value = "";
      if (!file) return;
      setPendingVideoFile(file);
  };

  const runVideoImport = async (sensitivity) => {
      const file = pendingVideoFile;
      setPendingVideoFile(null);
      if (!file) return;

      setImporting(true);
      const lt = toast.loading("Analyse de la vidéo en cours...");

      try {
          const updated = await appendVideoToProject(id, file, sensitivity);
          setProject(updated);
          setShots(updated.shots || []);
          toast.dismiss(lt);
          toast.success(`${updated.shots.length - shots.length} plans ajoutés depuis la vidéo`);
      } catch (err) {
          toast.dismiss(lt);
          toast.error("Échec de l'import vidéo");
          console.error('Video import error:', err);
      } finally {
          setImporting(false);
      }
  };

  const [davinciModalOpen, setDavinciModalOpen] = useState(false);

  const handleExport = async (format = 'pdf', options = {}) => {
      setExporting(true);
      try {
          await updateShots(id, shots);

          let endpoint, extension;
          if (format === 'zip') {
              endpoint = `/api/projects/${id}/download-zip`;
              extension = '_mobile.zip';
          } else if (format === 'thumbnails') {
              endpoint = `/api/projects/${id}/download-zip-thumbnails`;
              extension = '_vignettes.zip';
          } else if (format === 'davinci') {
              endpoint = `/api/projects/${id}/export-davinci?frame_rate=${encodeURIComponent(options.frameRate || 24)}`;
              extension = '_edit.zip';
          } else {
              endpoint = `/api/projects/${id}/download`;
              extension = '.pdf';
          }

          const response = await api.get(endpoint, { responseType: 'blob', timeout: 120000 });
          const blobUrl = URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = `${project.title || 'storyboard'}${extension}`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(blobUrl);

          const labels = { pdf: 'PDF', zip: 'ZIP Mobile', thumbnails: 'ZIP Vignettes', davinci: 'Export Logiciel' };
          toast.success(`Téléchargement ${labels[format]} lancé !`);
      }
      catch (err) { console.error('Export error:', err); toast.error("Échec de l'export"); }
      finally { setExporting(false); }
  };

  const handleDavinciConfirm = (frameRate) => {
      setDavinciModalOpen(false);
      handleExport('davinci', { frameRate });
  };

  const sel = selectedShotIds.size > 0 ? shots.find(s => s.id === [...selectedShotIds][selectedShotIds.size - 1]) : null;

  if (loading) return <div className="flex h-screen items-center justify-center"><Loader2 className="animate-spin w-5 h-5 text-muted-foreground" /></div>;
  if (!project) return <div className="p-8 text-muted-foreground text-sm">Projet introuvable</div>;

  return (
    <Layout title={project.title} actions={
        <div className="flex items-center gap-2">
            {!isOwner && (
                <span className="text-xs text-muted-foreground flex items-center gap-1.5 px-2 py-1 rounded-md border border-border bg-secondary/30">
                    Lecture seule
                </span>
            )}
            {project.status === 'processing' && (
                <span className="text-xs text-muted-foreground mr-2 flex items-center gap-1.5 text-primary">
                    <Loader2 className="w-3 h-3 animate-spin"/> Traitement
                </span>
            )}
            {isOwner && selectedShotIds.size > 0 && (
                <div className="flex items-center gap-1 mr-2 bg-primary/5 px-2 py-1 rounded-lg border border-primary/20">
                    <span className="text-xs mr-1.5 text-primary font-medium">{selectedShotIds.size}</span>
                    <button className="p-1 hover:bg-primary/10 rounded transition-colors" onClick={() => handleDuplicate()} data-testid="bulk-duplicate-btn"><Copy className="w-3 h-3 text-primary" /></button>
                    <button className="p-1 hover:bg-destructive/10 rounded transition-colors" onClick={() => handleDelete()} data-testid="bulk-delete-btn"><Trash2 className="w-3 h-3 text-destructive" /></button>
                </div>
            )}
            {!isOwner ? (
                <Button size="sm" className="h-8 text-xs gap-1.5" onClick={handleDuplicateProject} disabled={duplicatingProject} data-testid="duplicate-project-btn">
                    {duplicatingProject ? <Loader2 className="w-3 h-3 animate-spin" /> : <Copy className="w-3 h-3" />} Dupliquer
                </Button>
            ) : (
                <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5" onClick={handleSave} disabled={saving} data-testid="save-btn">
                    {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Enregistrer
                </Button>
            )}
            <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5" onClick={() => navigate(`/project/${id}/slideshow`)} disabled={!shots.length || project.status !== 'ready'} data-testid="slideshow-btn">
                <Play className="w-3 h-3" /> Lecture
            </Button>
            <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5" onClick={handleOpenShareModal} data-testid="share-btn">
                <Share2 className="w-3 h-3" /> Partager
            </Button>
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <Button size="sm" className="h-8 text-xs gap-1.5" disabled={exporting || project.status !== 'ready'} data-testid="export-btn">
                        {exporting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />} Export
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuLabel>Format d'export</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => handleExport('pdf')} className="cursor-pointer" data-testid="export-pdf-option">
                        <FileText className="w-4 h-4 mr-2" />
                        <div>
                            <div className="font-medium">PDF A4</div>
                            <div className="text-xs text-muted-foreground">6 vignettes par page</div>
                        </div>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleExport('zip')} className="cursor-pointer" data-testid="export-zip-option">
                        <Smartphone className="w-4 h-4 mr-2" />
                        <div>
                            <div className="font-medium">ZIP Mobile</div>
                            <div className="text-xs text-muted-foreground">Images 9:16 pour telephone</div>
                        </div>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleExport('thumbnails')} className="cursor-pointer" data-testid="export-thumbnails-option">
                        <ImageIcon className="w-4 h-4 mr-2" />
                        <div>
                            <div className="font-medium">ZIP Vignettes</div>
                            <div className="text-xs text-muted-foreground">Uniquement les images brutes</div>
                        </div>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => setDavinciModalOpen(true)} className="cursor-pointer" data-testid="export-davinci-option">
                        <Clapperboard className="w-4 h-4 mr-2" />
                        <div>
                            <div className="font-medium">Export Logiciel</div>
                            <div className="text-xs text-muted-foreground">FCPXML + médias en ZIP</div>
                        </div>
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
        </div>
    }>
      <div className="flex h-full overflow-hidden relative">
        <div className="flex-1 flex flex-col h-full overflow-hidden relative">
            <div className="flex-1 overflow-y-auto p-8 pb-32">
                {project.status === 'processing' ? (
                    <div className="w-full flex flex-col items-center justify-center text-muted-foreground mt-20">
                        <Loader2 className="w-8 h-8 animate-spin mb-4 text-primary" />
                        <p className="text-sm">Analyse de la vidéo...</p>
                    </div>
                ) : (
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
                        <SortableContext items={shots.map(s => s.id)} strategy={rectSortingStrategy}>
                            <div className="grid grid-cols-3 gap-8" data-testid="shots-grid">
                                {shots.map((shot, index) => (
                                    <SortableShot key={shot.id} shot={shot} index={index}
                                        isSelected={selectedShotIds.has(shot.id)} toggleSelection={toggleSelection} selectSingle={selectSingle}
                                        triggerCardUpload={triggerCardUpload} triggerEditor={triggerEditor} handleDuplicate={handleDuplicate} handleDelete={handleDelete} handleInsertAt={handleInsertAt}
                                        shotAspect="aspect-video" imgFit={project?.format === '9:16' ? 'object-contain' : 'object-cover'} />
                                ))}
                                {/* Add Shot Card - always at the end */}
                                <AddShotCard
                                    onAddBlank={() => handleInsertAt(-1, 'blank')}
                                    onImportVideo={() => videoInputRef.current?.click()}
                                />
                            </div>
                        </SortableContext>
                        <DragOverlay>{activeId ? <SortableShot shot={shots.find(s => s.id === activeId)} index={shots.findIndex(s => s.id === activeId)} isSelected={selectedShotIds.has(activeId)} isOverlay shotAspect="aspect-video" imgFit={project?.format === '9:16' ? 'object-contain' : 'object-cover'} /> : null}</DragOverlay>
                    </DndContext>
                )}
            </div>
            
            {/* Status bar */}
            <div className="h-7 border-t border-border bg-card flex items-center px-5 text-xs text-muted-foreground justify-between z-10 shrink-0 select-none">
                <span>{importing ? "Import en cours..." : `Selected: ${selectedShotIds.size}`}</span>
                <span>Total: {formatDuration(shots.reduce((a, s) => a + s.duration, 0))}</span>
            </div>
            
            <input type="file" ref={cardFileInputRef} className="hidden" accept="image/*" onChange={handleImageUpload} />
            <input type="file" ref={videoInputRef} className="hidden" accept="video/*" onChange={handleVideoImport} />
        </div>

        {/* Inspector - 320px */}
        {sel && (
            <div className="w-80 border-l border-border bg-card flex flex-col h-full z-20 shrink-0" style={{ animation: 'slideIn 200ms cubic-bezier(0.4, 0, 0.2, 1)' }} data-testid="shot-inspector">
                <div className="px-5 py-4 border-b border-border flex items-center justify-between">
                    <div>
                        <span className="text-2xl font-semibold tracking-tight text-foreground">#{shots.findIndex(s => s.id === sel.id) + 1}</span>
                        {selectedShotIds.size > 1 && <span className="text-xs text-muted-foreground ml-2">+{selectedShotIds.size - 1} more</span>}
                    </div>
                    <div className="flex items-center gap-1">
                        <button className="p-1.5 text-muted-foreground hover:text-destructive rounded transition-colors" onClick={() => handleDelete()} data-testid="inspector-delete-btn"><Trash2 className="w-3.5 h-3.5" /></button>
                        <button className="p-1.5 text-muted-foreground hover:text-foreground rounded transition-colors" onClick={() => setSelectedShotIds(new Set())} data-testid="inspector-close-btn"><Plus className="w-3.5 h-3.5 rotate-45" /></button>
                    </div>
                </div>
                
                <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">
                    {selectedShotIds.size === 1 && (
                        <div className="aspect-video rounded-lg overflow-hidden border border-border bg-secondary/20 relative group cursor-pointer" onClick={() => fileInputRef.current?.click()}>
                            {sel.frame_path_temp ? <img src={sel.frame_path_temp} className="w-full h-full object-contain" alt="" />
                             : sel.frame_path ? <img src={(process.env.REACT_APP_BACKEND_URL || '') + sel.frame_path} className="w-full h-full object-contain" alt="" />
                             : <div className="w-full h-full flex items-center justify-center"><span className="text-xs text-muted-foreground/40">Click to upload</span></div>}
                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                <Upload className="w-4 h-4 text-white" />
                            </div>
                            <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleImageUpload} />
                        </div>
                    )}

                    {selectedShotIds.size === 1 && (
                        <Button
                            variant="outline"
                            size="sm"
                            className="w-full gap-2 text-xs"
                            onClick={() => triggerEditor(sel.id)}
                            data-testid="inspector-edit-btn"
                        >
                            <Pencil className="w-3.5 h-3.5" /> Editer
                        </Button>
                    )}
                    
                    <div className="grid grid-cols-2 gap-3">
                        <div><Label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Start</Label><div className="text-sm p-2 bg-secondary/30 rounded-md text-muted-foreground">{selectedShotIds.size > 1 ? "-" : formatTime(sel.start_time)}</div></div>
                        <div><Label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Duration (s)</Label><Input type="number" step="0.1" min="0.1" className="h-8 text-sm ghost-input" defaultValue={sel.duration} key={sel.id + '-duration'} onBlur={(e) => handleShotUpdate('duration', parseFloat(e.target.value) || 0.1)} data-testid="inspector-duration" /></div>
                    </div>

                    <div>
                        <Label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Action / Description / Dialogue</Label>
                        <Textarea className="resize-none min-h-[100px] text-sm ghost-input leading-relaxed font-typewriter" value={sel.description} onChange={(e) => handleShotUpdate('description', e.target.value)} placeholder="Action, description, dialogue..." data-testid="inspector-description" />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div><Label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Shot Type</Label>
                            <Select value={sel.shot_type} onValueChange={(v) => handleShotUpdate('shot_type', v)}>
                                <SelectTrigger className="h-8 text-sm ghost-input" data-testid="inspector-shot-type"><SelectValue /></SelectTrigger>
                                <SelectContent>{["Wide","Medium","Close-up","Extreme Close-up","Over Shoulder","POV","Insert"].map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
                            </Select></div>
                        <div><Label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Camera</Label>
                            <Select value={sel.camera_movement} onValueChange={(v) => handleShotUpdate('camera_movement', v)}>
                                <SelectTrigger className="h-8 text-sm ghost-input" data-testid="inspector-camera-move"><SelectValue /></SelectTrigger>
                                <SelectContent>{["Static","Pan","Tilt","Zoom In","Zoom Out","Tracking","Crane","Dolly","Handheld","Steadicam","Plongée","Contre-plongée"].map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
                            </Select></div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div><Label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Focal</Label>
                            <Select value={sel.focal_length || '50mm'} onValueChange={(v) => handleShotUpdate('focal_length', v)}>
                                <SelectTrigger className="h-8 text-sm ghost-input" data-testid="inspector-focal"><SelectValue /></SelectTrigger>
                                <SelectContent>{["14mm","18mm","24mm","35mm","50mm","85mm","100mm","135mm","200mm"].map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
                            </Select></div>
                        <div><Label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Lighting</Label>
                            <Select value={sel.lighting || 'Day'} onValueChange={(v) => handleShotUpdate('lighting', v)}>
                                <SelectTrigger className="h-8 text-sm ghost-input" data-testid="inspector-lighting"><SelectValue /></SelectTrigger>
                                <SelectContent>{["Day","Night","Ext. Day","Ext. Night","Int. Day","Int. Night","Golden Hour","Blue Hour","Overcast","Artificial"].map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
                            </Select></div>
                    </div>
                    <div>
                        <Label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Notes</Label>
                        <Textarea className="resize-none min-h-[60px] text-sm ghost-input leading-relaxed" value={sel.notes || ''} onChange={(e) => handleShotUpdate('notes', e.target.value)} placeholder="Notes techniques..." data-testid="inspector-notes" />
                    </div>

                    {isAdmin && selectedShotIds.size === 1 && (
                        <div className="rounded-md border border-border bg-secondary/20 p-3 space-y-1.5" data-testid="inspector-admin-prompt">
                            <Label className="text-xs text-muted-foreground uppercase tracking-wider block">Prompt envoyé (admin)</Label>
                            {sel.ai_config?.last_prompt ? (
                                <pre className="text-[11px] leading-relaxed whitespace-pre-wrap break-words font-mono text-foreground/90 max-h-72 overflow-y-auto">{sel.ai_config.last_prompt}</pre>
                            ) : (
                                <p className="text-[11px] italic text-muted-foreground">
                                    Aucun prompt enregistré pour ce plan. Génère une image pour le voir apparaître ici.
                                </p>
                            )}
                        </div>
                    )}
                </div>
            </div>
        )}
      </div>

      {/* Share Modal */}
      <Dialog open={showShareModal} onOpenChange={setShowShareModal}>
          <DialogContent className="sm:max-w-md overflow-y-auto max-h-[90vh]">
              <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                      <Share2 className="w-5 h-5" /> Partager le projet
                  </DialogTitle>
                  <DialogDescription>
                      Configurez les options puis enregistrez pour créer ou mettre à jour le lien.
                  </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 py-2">
                  {/* Exports */}
                  <div className="space-y-2">
                      <p className="text-xs text-muted-foreground font-semibold uppercase tracking-wider">Exports autorisés</p>
                      {[
                          { icon: FileText, label: "Export PDF", state: allowPdfExport, setter: setAllowPdfExport },
                          { icon: Smartphone, label: "Export ZIP Mobile", state: allowZipExport, setter: setAllowZipExport },
                          { icon: ImageIcon, label: "Partage de vignette", state: shareThumbsEnabled, setter: setShareThumbsEnabled },
                          { icon: Clapperboard, label: "Export Logiciel (FCPXML)", state: shareEditEnabled, setter: setShareEditEnabled },
                      ].map(({ icon: Icon, label, state, setter }) => (
                          <div key={label} className="flex items-center justify-between gap-4">
                              <div className="flex items-center gap-2 min-w-0 flex-1">
                                  <Icon className="w-4 h-4 text-muted-foreground shrink-0" />
                                  <Label className="text-sm">{label}</Label>
                              </div>
                              <Switch checked={state} onCheckedChange={setter} className="shrink-0" />
                          </div>
                      ))}
                  </div>

                  <div className="border-t border-border/50" />

                  {/* Password */}
                  <div className="space-y-2">
                      <div className="flex items-center justify-between gap-4">
                          <div className="flex items-center gap-2">
                              <Lock className="w-4 h-4 text-muted-foreground shrink-0" />
                              <Label className="text-sm">Protéger par mot de passe</Label>
                          </div>
                          <Switch checked={sharePasswordEnabled} onCheckedChange={(v) => { setSharePasswordEnabled(v); if (!v) setSharePassword(''); }} className="shrink-0" />
                      </div>
                      {sharePasswordEnabled && (
                          <div className="relative">
                              <Input
                                  type="password"
                                  placeholder={shareHasPassword ? "••••••  (laissez vide pour conserver)" : "Mot de passe…"}
                                  value={sharePassword}
                                  onChange={(e) => setSharePassword(e.target.value)}
                                  className="h-9 text-sm pr-9"
                                  autoComplete="new-password"
                              />
                          </div>
                      )}
                      {sharePasswordEnabled && (
                          <p className="text-xs text-muted-foreground">
                              Les visiteurs devront saisir ce mot de passe avant d'accéder au projet.
                          </p>
                      )}
                  </div>

                  {/* Comments */}
                  <div className="space-y-1">
                      <div className="flex items-center justify-between gap-4">
                          <div className="flex items-center gap-2">
                              <MessageSquare className="w-4 h-4 text-muted-foreground shrink-0" />
                              <Label className="text-sm">Autoriser les commentaires</Label>
                          </div>
                          <Switch checked={shareCommentsEnabled} onCheckedChange={setShareCommentsEnabled} className="shrink-0" />
                      </div>
                      {shareCommentsEnabled && (
                          <p className="text-xs text-muted-foreground pl-6">
                              Les utilisateurs connectés pourront commenter chaque frame.
                          </p>
                      )}
                  </div>

                  {/* Duplicate */}
                  <div className="space-y-1">
                      <div className="flex items-center justify-between gap-4">
                          <div className="flex items-center gap-2">
                              <CopyPlus className="w-4 h-4 text-muted-foreground shrink-0" />
                              <Label className="text-sm">Partage de projet</Label>
                          </div>
                          <Switch checked={shareDuplicateEnabled} onCheckedChange={setShareDuplicateEnabled} className="shrink-0" />
                      </div>
                      {shareDuplicateEnabled && (
                          <p className="text-xs text-muted-foreground pl-6">
                              Les utilisateurs connectés pourront dupliquer ce projet dans leur espace.
                          </p>
                      )}
                  </div>

                  <div className="border-t border-border/50" />

                  {/* Link display */}
                  {shareToken && (
                      <div className="flex items-center gap-2 p-3 bg-secondary/50 rounded-lg overflow-hidden">
                          <Link className="w-4 h-4 text-muted-foreground shrink-0" />
                          <code className="text-xs flex-1 truncate">/shared/{shareToken}</code>
                          <Button size="sm" variant="ghost" onClick={copyShareLink} className="shrink-0 h-7 w-7 p-0">
                              <Copy className="w-3.5 h-3.5" />
                          </Button>
                      </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-2">
                      <Button onClick={handleSaveShareSettings} disabled={sharingLoading} className="flex-1 gap-2">
                          {sharingLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
                          {shareToken ? "Enregistrer" : "Créer le lien"}
                      </Button>
                      {shareToken && (
                          <>
                              <Button variant="outline" onClick={copyShareLink} className="gap-1.5 shrink-0">
                                  <Copy className="w-3.5 h-3.5" /> Copier
                              </Button>
                              <Button variant="destructive" onClick={handleDeleteShareLink} disabled={sharingLoading} className="shrink-0 px-3" title="Supprimer le lien de partage">
                                  {sharingLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Unlink className="w-4 h-4" />}
                              </Button>
                          </>
                      )}
                  </div>
              </div>
          </DialogContent>
      </Dialog>

      <DavinciExportModal
        open={davinciModalOpen}
        onOpenChange={setDavinciModalOpen}
        onConfirm={handleDavinciConfirm}
      />

      {/* Sensitivity picker for adding a new video — mirrors the choice the
          user gets at initial project creation, so they can match an action
          scene's tighter cuts or a slow scene's longer takes. */}
      <Dialog open={!!pendingVideoFile} onOpenChange={(o) => { if (!o) setPendingVideoFile(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Video className="w-5 h-5" /> Sensibilité de détection
            </DialogTitle>
            <DialogDescription>
              Plus la sensibilité est élevée, plus de coupes seront détectées.
              Choisissez selon le rythme de votre vidéo.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-2 py-2">
            {[
              { key: 'low', label: 'Faible', desc: 'Idéal pour des plans longs avec peu de coupes (interview, paysage).' },
              { key: 'standard', label: 'Standard', desc: 'Réglage par défaut, adapté à la plupart des montages.' },
              { key: 'sensitive', label: 'Élevée', desc: 'Pour des montages rapides ou des scènes d\'action très découpées.' },
            ].map((opt) => (
              <button
                key={opt.key}
                type="button"
                onClick={() => runVideoImport(opt.key)}
                className="text-left rounded-lg border border-border hover:border-primary/50 hover:bg-primary/5 transition-colors p-3"
                data-testid={`video-import-sensitivity-${opt.key}`}
              >
                <div className="text-sm font-medium">{opt.label}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{opt.desc}</div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <ShotEditorModal
        open={showEditor}
        onOpenChange={setShowEditor}
        projectId={id}
        shotId={editorTargetShotId}
        shotData={editorTargetShotId ? shots.find((s) => s.id === editorTargetShotId) : null}
        onSaved={handleEditorSaved}
        format={project?.format || '16:9'}
        blanking={project?.blanking ?? null}
        onBlankingChange={async (ratio) => {
          try {
            await setProjectBlanking(id, ratio);
            setProject((p) => p ? { ...p, blanking: ratio ?? null } : p);
          } catch { /* silent */ }
        }}
      />
    </Layout>
  );
}
